import { ApiService } from '../ApiService';
import type { ApiResponse } from '../types';
import type { 
  AppSettings, 
  SettingsUpdateRequest,
  DataSharingRequest,
  DataSharingResponse,
  DataSourceUpdateRequest,
  DataSourceResponse,
  IntegrationStatusResponse,
  IntegrationSettings,
  BankIntegrationConfig,
  EInvoiceIntegrationConfig,
  TaxIntegrationConfig,
  PortfolioIntegrationConfig,
  UpdateExchangeRatesRequest
} from '../../../types/settings';

export const settingsApi = {
  // Récupérer tous les paramètres
  getSettings: (): Promise<ApiResponse<AppSettings>> =>
    ApiService.get<AppSettings>('/settings'),

  // Mettre à jour une catégorie de paramètres
  updateSettings: (request: SettingsUpdateRequest): Promise<ApiResponse<AppSettings>> =>
    ApiService.put<AppSettings>(`/settings/${request.category}`, request.settings),

  // Récupérer une catégorie spécifique
  getSettingsCategory: (category: keyof AppSettings): Promise<ApiResponse<AppSettings[keyof AppSettings]>> =>
    ApiService.get<AppSettings[keyof AppSettings]>(`/settings/${category}`),

  // Réinitialiser les paramètres par défaut
  resetSettings: (category?: keyof AppSettings): Promise<ApiResponse<AppSettings>> => {
    const url = category ? `/settings/${category}/reset` : '/settings/reset';
    return ApiService.post<AppSettings>(url, {});
  },

  // Importer des paramètres
  importSettings: (settings: Partial<AppSettings>): Promise<ApiResponse<AppSettings>> =>
    ApiService.post<AppSettings>('/settings/import', settings),

  // Exporter les paramètres
  exportSettings: (): Promise<ApiResponse<AppSettings>> =>
    ApiService.get<AppSettings>('/settings/export'),

  // Valider les paramètres
  validateSettings: (settings: Partial<AppSettings>): Promise<ApiResponse<{ valid: boolean; errors?: string[] }>> =>
    ApiService.post<{ valid: boolean; errors?: string[] }>('/settings/validate', settings),

  // === TAUX DE CHANGE ===
  
  /**
   * Mettre à jour les taux de change (endpoint dédié)
   * @see PUT /settings/exchange-rates
   */
  updateExchangeRates: (request: UpdateExchangeRatesRequest): Promise<ApiResponse<{ message: string }>> =>
    ApiService.put<{ message: string }>('/settings/exchange-rates', request),

  // === NOUVEAUX ENDPOINTS POUR LES INTÉGRATIONS AVANCÉES ===
  
  // Partage de données
  getDataSharingSettings: (): Promise<ApiResponse<DataSharingResponse['data']>> =>
    ApiService.get<DataSharingResponse['data']>('/settings/data-sharing'),

  updateDataSharingSettings: (request: DataSharingRequest): Promise<ApiResponse<DataSharingResponse['data']>> =>
    ApiService.put<DataSharingResponse['data']>('/settings/data-sharing', request),

  // Sources de données
  getDataSourceSettings: (): Promise<ApiResponse<DataSourceResponse['data']>> =>
    ApiService.get<DataSourceResponse['data']>('/settings/data-sources'),

  updateDataSourceSettings: (request: DataSourceUpdateRequest): Promise<ApiResponse<DataSourceResponse['data']>> =>
    ApiService.put<DataSourceResponse['data']>('/settings/data-sources', request),

  // Statut des intégrations
  getIntegrationStatus: (): Promise<ApiResponse<IntegrationStatusResponse['data']>> =>
    ApiService.get<IntegrationStatusResponse['data']>('/settings/integrations/status'),

  // Configuration bancaire (endpoints dédiés)
  getBankIntegrations: (): Promise<ApiResponse<BankIntegrationConfig[]>> =>
    ApiService.get<BankIntegrationConfig[]>('/settings/integrations/banks'),

  updateBankIntegration: (bankId: string, config: Partial<BankIntegrationConfig>): Promise<ApiResponse<BankIntegrationConfig>> =>
    ApiService.put<BankIntegrationConfig>(`/settings/integrations/banks/${bankId}`, config),

  // Mise à jour partielle des intégrations (route ALL-IN-ONE)
  // Envoyer uniquement les sous-objets à modifier — l'API fait un merge partiel.
  updateIntegrations: (payload: Partial<IntegrationSettings>): Promise<ApiResponse<IntegrationSettings>> =>
    ApiService.put<IntegrationSettings>('/settings/integrations', payload),

  // Helpers : lire une section d'intégration via GET /settings (pas d'endpoint dédié)
  getEInvoiceSettings: async (): Promise<ApiResponse<EInvoiceIntegrationConfig>> => {
    const res = await ApiService.get<AppSettings>('/settings');
    if (!res.success || !res.data) return { success: false, error: res.error } as any;
    return { success: true, data: res.data.integrations.eInvoicing };
  },

  getTaxIntegrationSettings: async (): Promise<ApiResponse<TaxIntegrationConfig>> => {
    const res = await ApiService.get<AppSettings>('/settings');
    if (!res.success || !res.data) return { success: false, error: res.error } as any;
    return { success: true, data: res.data.integrations.taxIntegration };
  },

  getPortfolioIntegrationSettings: async (): Promise<ApiResponse<PortfolioIntegrationConfig>> => {
    const res = await ApiService.get<AppSettings>('/settings');
    if (!res.success || !res.data) return { success: false, error: res.error } as any;
    return { success: true, data: res.data.integrations.portfolioIntegration };
  },
};
